import { forwardRef } from "react";

import { styled } from "styled-components";
import { t } from "translations";

import { RadioGroup, RadioGroupOption, RadioGroupOptionProps } from "ui";
import { SmallText } from "ui/styles/text";

import { RatePlan } from "../types";
import {
  periodToProductNameKey,
  periodToProductPriceKey,
} from "../utils/productLocalization";

const Background = styled.div(
  ({ theme }) => `
  background-color: ${theme.colors.grayDarkest};
  border-radius: 12px;

  & > fieldset > label {
    border-bottom: 1px solid #222428;
  }

  /* Remove border clipping background on last element */
  & > fieldset > label:last-child {
    border-radius: 0 0 12px 12px;
  }
  `,
);

const LabelText = styled(SmallText)(
  ({ theme }) => `
  display: flex;
  gap: 8px;
  color: ${theme.colors.white2};

  & > span {
    color: ${theme.colors.grayLight};
  }
`,
);

interface ProductSelectorProps
  extends Omit<RadioGroupOptionProps, "labelText"> {
  ratePlans: RatePlan[];
}

export const ProductSelector = forwardRef<
  HTMLInputElement,
  ProductSelectorProps
>(({ ratePlans, ...radioProps }, ref) => (
  <Background>
    <RadioGroup>
      {ratePlans.map(({ billingPeriod, amount, currency }) => {
        const price = `${amount.toLocaleString(undefined, {
          style: "currency",
          currency: currency,
        })} ${currency}`;

        const priceText = t(periodToProductPriceKey(billingPeriod), {
          subscription_price: price,
        });

        const labelText = (
          <LabelText>
            {t(periodToProductNameKey(billingPeriod))}
            <span>{priceText}</span>
          </LabelText>
        );
        return (
          <RadioGroupOption
            {...radioProps}
            ref={ref}
            key={billingPeriod}
            value={billingPeriod}
            labelText={labelText}
            data-testid={periodToProductNameKey(billingPeriod)}
          />
        );
      })}
    </RadioGroup>
  </Background>
));
